/**
 * Agent Marketplace Types
 * UF-008: Agent Marketplace UI
 */

// =============================================================================
// Agent Types
// =============================================================================

/** Agent category for filtering */
export type AgentCategory = "ml" | "analytics" | "data-processing" | "custom";

/** Pricing model */
export type PricingModel = "free" | "paid" | "subscription";

/** Sort options */
export type SortOption = "popular" | "newest" | "rating" | "price-low" | "price-high";

/** Rating filter options */
export type RatingFilter = 4 | 3 | 2 | 1 | null;

/** Agent capability */
export interface AgentCapability {
  id: string;
  name: string;
  description: string;
}

/** Agent pricing details */
export interface AgentPricing {
  model: PricingModel;
  price?: number;
  currency?: string;
  billingPeriod?: "one-time" | "monthly" | "yearly";
}

/** Agent review */
export interface AgentReview {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  rating: number;
  comment: string;
  createdAt: string;
  helpful: number;
}

/** Marketplace agent */
export interface MarketplaceAgent {
  id: string;
  name: string;
  shortDescription: string;
  fullDescription: string;
  icon?: string;
  iconColor?: string;
  category: AgentCategory;
  capabilities: AgentCapability[];
  pricing: AgentPricing;
  rating: number;
  reviewCount: number;
  downloads: number;
  publisher: string;
  publisherVerified: boolean;
  version: string;
  lastUpdated: string;
  createdAt: string;
  tags: string[];
  reviews: AgentReview[];
  owned?: boolean;
  deployed?: boolean;
}

// =============================================================================
// Filter State
// =============================================================================

export interface MarketplaceFilters {
  search: string;
  categories: AgentCategory[];
  pricingModels: PricingModel[];
  minRating: RatingFilter;
  sortBy: SortOption;
}

// =============================================================================
// Category Display Config
// =============================================================================

export const CATEGORY_CONFIG: Record<AgentCategory, { label: string; color: string }> = {
  ml: { label: "Machine Learning", color: "#758eff" },
  analytics: { label: "Analytics", color: "#d61ef4" },
  "data-processing": { label: "Data Processing", color: "#4aff7a" },
  custom: { label: "Custom", color: "#E6BEFA" },
};

export const PRICING_CONFIG: Record<PricingModel, { label: string; badge: string }> = {
  free: { label: "Free", badge: "bg-hvme-green/20 text-hvme-green" },
  paid: { label: "Paid", badge: "bg-hvme-primary/20 text-hvme-primary" },
  subscription: { label: "Subscription", badge: "bg-hvme-accent/20 text-hvme-accent" },
};

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "popular", label: "Most Popular" },
  { value: "newest", label: "Newest" },
  { value: "rating", label: "Highest Rated" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
];
